import { AxiosInstance } from 'axios';
import {
  ApplyFixedPriceImportRequest,
  CreateManualOfferRequest,
  PreviewFixedPriceImportRequest,
  FixedPriceImportPreview,
  FixedPriceImportResult,
  ListPreisToolItemsFilters,
  ListSupplierAnomaliesFilters,
  PreisToolDisposition,
  PreisToolDispositionSettings,
  PreisToolDispositionSettingsValues,
  PreisToolFacetsResponse,
  PreisToolFixedPricesResponse,
  PreisToolItemDetailResponse,
  PreisToolItemsResponse,
  PreisToolLegendThresholds,
  PreisToolManualOffer,
  PreisToolPriceChangeBatchResponse,
  PreisToolSupplierTerms,
  PreisToolPriceHistoryResponse,
  PreisToolPriceWritebackStatusResponse,
  PreisToolSalesHistoryResponse,
  PreisToolSupplierAnomaliesResponse,
  PreisToolSupplierOffersResponse,
  PreisToolSuppliersResponse,
  PreisToolUpstreamSupplierNamesResponse,
  PriceListColumnNamesResponse,
  SaveFixedPriceRequest,
  SavePriceListColumnNamesRequest,
  SaveUpstreamSupplierRequest,
  SavePreisToolLegendThresholdsRequest,
  SavePreisToolPricesRequest,
  SavePreisToolPricesResponse,
} from './preis-tool.types';

const BASE = '/v1/preis-tool';

export const createPreisToolApi = (api: AxiosInstance) => ({
  list: (filters?: ListPreisToolItemsFilters) =>
    api
      .get<PreisToolItemsResponse>(`${BASE}/items`, { params: filters })
      .then((r) => r.data),

  facets: () =>
    api
      .get<PreisToolFacetsResponse>(`${BASE}/items/facets`)
      .then((r) => r.data),

  itemDetail: (itemId: string) =>
    api
      .get<PreisToolItemDetailResponse>(`${BASE}/items/detail`, {
        params: { itemId },
      })
      .then((r) => r.data),

  supplierOffers: (itemId: string) =>
    api
      .get<PreisToolSupplierOffersResponse>(`${BASE}/items/supplier-offers`, {
        params: { itemId },
      })
      .then((r) => r.data),

  supplierAnomalies: (filters?: ListSupplierAnomaliesFilters) =>
    api
      .get<PreisToolSupplierAnomaliesResponse>(`${BASE}/supplier-anomalies`, {
        params: filters,
      })
      .then((r) => r.data),

  legendThresholds: () =>
    api
      .get<PreisToolLegendThresholds>(`${BASE}/legend-thresholds`)
      .then((r) => r.data),

  saveLegendThresholds: (body: SavePreisToolLegendThresholdsRequest) =>
    api
      .put<PreisToolLegendThresholds>(`${BASE}/legend-thresholds`, body)
      .then((r) => r.data),

  priceHistory: (itemId: string, days: number) =>
    api
      .get<PreisToolPriceHistoryResponse>(`${BASE}/items/price-history`, {
        params: { itemId, days },
      })
      .then((r) => r.data),

  suppliers: () =>
    api
      .get<PreisToolSuppliersResponse>(`${BASE}/suppliers`)
      .then((r) => r.data),

  fixedPrices: (itemId: string) =>
    api
      .get<PreisToolFixedPricesResponse>(`${BASE}/items/fixed-prices`, {
        params: { itemId },
      })
      .then((r) => r.data),

  saveFixedPrice: (body: SaveFixedPriceRequest) =>
    api
      .put<PreisToolFixedPricesResponse>(`${BASE}/items/fixed-prices`, body)
      .then((r) => r.data),

  removeFixedPrice: (id: string) =>
    api
      .delete<PreisToolFixedPricesResponse>(`${BASE}/items/fixed-prices/${id}`)
      .then((r) => r.data),

  upstreamSupplierNames: () =>
    api
      .get<PreisToolUpstreamSupplierNamesResponse>(`${BASE}/upstream-suppliers`)
      .then((r) => r.data),

  saveUpstreamSupplier: (
    fixedPriceId: string,
    body: SaveUpstreamSupplierRequest,
  ) =>
    api
      .put<PreisToolFixedPricesResponse>(
        `${BASE}/items/fixed-prices/${fixedPriceId}/upstream-suppliers`,
        body,
      )
      .then((r) => r.data),

  removeUpstreamSupplier: (fixedPriceId: string, upstreamSupplierId: string) =>
    api
      .delete<PreisToolFixedPricesResponse>(
        `${BASE}/items/fixed-prices/${fixedPriceId}/upstream-suppliers/${upstreamSupplierId}`,
      )
      .then((r) => r.data),

  createManualOffer: (body: CreateManualOfferRequest) =>
    api
      .post<PreisToolManualOffer>(`${BASE}/items/manual-offers`, body)
      .then((r) => r.data),

  removeManualOffer: (id: string) =>
    api
      .delete<{ id: string; itemId: string }>(
        `${BASE}/items/manual-offers/${id}`,
      )
      .then((r) => r.data),

  /** Without `targetMargin` the set default target margin applies. */
  disposition: (itemId: string, targetMargin?: number) =>
    api
      .get<PreisToolDisposition>(`${BASE}/items/disposition`, {
        params: { itemId, targetMargin },
      })
      .then((r) => r.data),

  setReorderPoint: (itemId: string, reorderPoint: number | null) =>
    api
      .put<PreisToolDisposition>(`${BASE}/items/reorder-point`, {
        itemId,
        reorderPoint,
      })
      .then((r) => r.data),

  salesHistory: (itemId: string) =>
    api
      .get<PreisToolSalesHistoryResponse>(`${BASE}/items/sales-history`, {
        params: { itemId },
      })
      .then((r) => r.data),

  // Purchasing aid settings (EOS-62)
  dispositionSettings: () =>
    api
      .get<PreisToolDispositionSettings>(`${BASE}/disposition-settings`)
      .then((r) => r.data),

  saveDispositionSettings: (body: PreisToolDispositionSettingsValues) =>
    api
      .put<PreisToolDispositionSettings>(`${BASE}/disposition-settings`, body)
      .then((r) => r.data),

  previewFixedPriceImport: (request: PreviewFixedPriceImportRequest) =>
    api
      .post<FixedPriceImportPreview>(
        `${BASE}/fixed-price-imports/preview`,
        request,
      )
      .then((r) => r.data),

  applyFixedPriceImport: (request: ApplyFixedPriceImportRequest) =>
    api
      .post<FixedPriceImportResult>(`${BASE}/fixed-price-imports/apply`, request)
      .then((r) => r.data),

  priceListColumnNames: () =>
    api
      .get<PriceListColumnNamesResponse>(`${BASE}/price-list-column-names`)
      .then((r) => r.data),

  savePriceListColumnNames: (body: SavePriceListColumnNamesRequest) =>
    api
      .put<PriceListColumnNamesResponse>(
        `${BASE}/price-list-column-names`,
        body,
      )
      .then((r) => r.data),

  priceWritebackStatus: () =>
    api
      .get<PreisToolPriceWritebackStatusResponse>(
        `${BASE}/price-writeback/status`,
      )
      .then((r) => r.data),

  savePrices: (body: SavePreisToolPricesRequest) =>
    api
      .patch<SavePreisToolPricesResponse>(`${BASE}/items/prices`, body)
      .then((r) => r.data),

  saveSupplierTerms: (supplierBpId: number, shippingCost: number | null) =>
    api
      .put<PreisToolSupplierTerms>(`${BASE}/supplier-terms/${supplierBpId}`, {
        shippingCost,
      })
      .then((r) => r.data),

  priceChangeBatch: (batchId: string) =>
    api
      .get<PreisToolPriceChangeBatchResponse>(`${BASE}/price-changes`, {
        params: { batchId },
      })
      .then((r) => r.data),
});
